import {
  ImportedShotGroup,
  ImportedTargetDefinition,
  ImportedTargetGeometry,
  ImportedTargetProfile,
  ImportedRingDefinition
} from './imported-target.model';

const PROFILE_BY_TARGET_NAME: Record<string, ImportedTargetProfile> = {
  AP10: 'pistol',
  AR10: 'air rifle',
  CROAR: 'air serial'
};

const RINGS_BY_PROFILE: Record<ImportedTargetProfile, ImportedRingDefinition[]> = {
  pistol: [155.5, 139.5, 123.5, 107.5, 91.5, 75.5, 59.5, 43.5, 27.5, 11.5, 5.0].map((diameterMm, index) => ({ value: index + 1, diameterMm })),
  'air serial': [83, 74, 65, 56, 47, 38, 29, 20, 11, 2].map((diameterMm, index) => ({ value: index + 1, diameterMm })),
  'air rifle': [45.5, 40.5, 35.5, 30.5, 25.5, 20.5, 15.5, 10.5, 5.5, 0.5].map((diameterMm, index) => ({ value: index + 1, diameterMm }))
};

function requiredProperty(properties: Element[], identifier: string): string {
  const value = properties.find(property => property.getAttribute('identifier') === identifier)?.getAttribute('value')?.trim();
  if (!value) {
    throw new Error(`Manjka zahtevana XML lastnost: ${identifier}.`);
  }
  return value;
}

function optionalProperty(properties: Element[], identifier: string): string | null {
  return properties.find(property => property.getAttribute('identifier') === identifier)?.getAttribute('value')?.trim() || null;
}

export function isDecimalPistolTarget(target: Pick<ImportedTargetDefinition, 'profile' | 'displayName' | 'primaryScoring'>): boolean {
  if (target.profile !== 'pistol') return false;

  return /AirPistol_Final/i.test(target.primaryScoring || '') || /\bdec(?:imal)?\b/i.test(target.displayName);
}

function parseShotGroups(document: Document): ImportedShotGroup[] {
  return Array.from(document.querySelectorAll('Action[type*="ActionShotGroup"]')).map((action, index) => {
    const properties = Array.from(action.querySelectorAll(':scope > Properties > Property'));
    const rawCount = requiredProperty(properties, 'NumberOfShots');
    const numberOfShots = Number(rawCount);
    if (!Number.isInteger(numberOfShots) || numberOfShots < 0) {
      throw new Error(`Neveljavno število strelov v skupini ${index + 1}: ${rawCount}.`);
    }
    return {
      name: action.parentElement?.getAttribute('name') || `S${index}`,
      numberOfShots,
      fireKind: optionalProperty(properties, 'FireKind') || 'unknown'
    };
  });
}

export function parseImportedTargetXml(xmlText: string): ImportedTargetDefinition {
  if (!xmlText.trim()) {
    throw new Error('XML datoteka je prazna.');
  }

  const document = new DOMParser().parseFromString(xmlText, 'application/xml');
  if (document.querySelector('parsererror') || document.documentElement?.nodeName !== 'Template') {
    throw new Error('Datoteka XML ni veljavna SIUS predloga (pričakovan je koren Template).');
  }

  const properties = Array.from(document.querySelectorAll('Template > Properties > Property'));
  const scoringProperties = Array.from(document.querySelectorAll('Action[type*="ActionSetScoreMethods"] > Properties > Property'));
  const displayName = requiredProperty(properties, 'DisplayName');
  const targetName = requiredProperty(
    Array.from(document.querySelectorAll('Action[type*="ActionInitFrameTarget"] > Properties > Property')),
    'TargetName'
  ).toUpperCase();
  const profile = PROFILE_BY_TARGET_NAME[targetName];
  if (!profile) {
    throw new Error(`XML tarča ${targetName} nima podprte geometrije.`);
  }

  const shotGroups = parseShotGroups(document);
  if (shotGroups.length === 0) {
    throw new Error('XML tarča nima nobene skupine strelov.');
  }

  const geometry: ImportedTargetGeometry = {
    source: 'built-in-profile',
    profile,
    rings: RINGS_BY_PROFILE[profile].map(ring => ({ ...ring }))
  };

  return {
    schema: 'sius-template',
    displayName,
    targetName,
    profile,
    primaryScoring: optionalProperty(scoringProperties, 'PrimaryScoring'),
    scoring: optionalProperty(scoringProperties, 'Scoring'),
    group: document.querySelector('Template > Groups > Group')?.getAttribute('name') || null,
    shotGroups,
    coordinateSystem: {
      units: 'millimetres',
      origin: 'target-centre',
      xDirection: 'right',
      yDirection: 'up',
      source: 'existing-shot-coordinates'
    },
    geometry
  };
}
